
import React, { useState } from 'react';
import { SearchDropdown } from '@/components/SearchDropdown';
import { Link } from 'react-router-dom';
import { useAdminCheck } from '@/hooks/useAdminCheck';
import { Button } from '@/components/ui/button';
import { User, LogOut, Settings, Shield, Menu, X, Wrench } from 'lucide-react';
import { AuthDialog } from '@/components/AuthDialog';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/contexts/LanguageContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '@/components/ui/sheet';
import { MonthlyResetCountdown } from '@/components/MonthlyResetCountdown';
import { getHomePath, getFavoritesPath, getProfilePath, getLeaderboardPath, getMyVotesPath, getCreatorToolsPath, getAddServerPath } from '@/utils/i18nRouting';


export const Header: React.FC = () => {
  const { user, signOut } = useAuth();
  const { isAdmin } = useAdminCheck();
  const { t, language } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="border-b border-slate-800 bg-slate-900/95 backdrop-blur-sm sticky top-0 z-50">
      <div className="container mx-auto px-3 lg:px-4">
        {/* Header principal */}
        <div className="flex items-center justify-between gap-2 lg:gap-3 h-20 lg:h-24">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0">
            <Link 
              to={getHomePath(language)}
              className="flex items-center"
              aria-label={language === 'fr' ? 'Retour à l\'accueil' : language === 'es' ? 'Volver al inicio' : 'Back to home'}
            >
              <img 
                src="/assets/logo.png" 
                alt="RankServeur Logo - Classement Serveurs Privés Gaming"
                width="96"
                height="96"
                className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 lg:w-20 lg:h-20 drop-shadow-[0_0_12px_rgba(59,130,246,0.6)] hover:drop-shadow-[0_0_20px_rgba(245,158,11,0.7)] transition-all duration-500 hover:scale-110 animate-pulse-subtle"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                style={{ aspectRatio: '1/1' }}
              />
            </Link>
          </div>

          {/* Navigation Desktop */}
          <nav className="hidden md:flex items-center space-x-2 lg:space-x-3 xl:space-x-4 flex-shrink-0">
            <Link to={getHomePath(language)} className="text-slate-300 hover:text-white transition-colors text-xs lg:text-sm whitespace-nowrap">
              {t('nav.home')}
            </Link>
            <Link to={getLeaderboardPath(language)} className="text-slate-300 hover:text-white transition-colors text-xs lg:text-sm whitespace-nowrap">
              {t('nav.topVoters')}
            </Link>
            {user && (
              <>
                <Link to={getFavoritesPath(language)} className="text-slate-300 hover:text-white transition-colors text-xs lg:text-sm whitespace-nowrap hidden lg:inline">
                  {t('nav.favorites')}
                </Link>
                <Link to={getMyVotesPath(language)} className="text-slate-300 hover:text-white transition-colors text-xs lg:text-sm whitespace-nowrap hidden lg:inline">
                  {t('nav.myVotes')}
                </Link>
              </>
            )}
            <Link 
              to={getAddServerPath(language)}
              className="text-gaming-accent hover:text-gaming-accent/80 transition-colors text-xs lg:text-sm font-semibold whitespace-nowrap"
            >
              {t('nav.addServer')}
            </Link>
          </nav>

          {/* Section droite */}
          <div className="flex items-center gap-1.5 lg:gap-2 flex-shrink-0">
            {/* Compteur Reset Mensuel - visible sur grand écran seulement */}
            <div className="hidden 2xl:block flex-shrink-0">
              <MonthlyResetCountdown variant="mini" />
            </div>

            {/* Language Selector - masqué sur petits écrans */}
            <div className="hidden md:block flex-shrink-0">
              <LanguageSelector />
            </div>
            
            {/* Auth Section - version compacte */}
            <div className="hidden lg:flex items-center gap-1.5 flex-shrink-0">
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="flex items-center gap-1.5 text-slate-300 hover:text-white hover:bg-slate-800 px-2"
                    >
                      <User className="w-4 h-4" />
                      <span className="hidden xl:inline font-mono text-xs">
                        {user.email?.split('@')[0]}
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-slate-800 border-slate-700">
                    <DropdownMenuItem asChild>
                      <Link 
                        to={getProfilePath(language)}
                        className="flex items-center space-x-2 text-slate-300 hover:text-white cursor-pointer"
                      >
                        <Settings className="w-4 h-4" />
                        <span>{t('nav.profile')}</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link 
                        to={getCreatorToolsPath(language)}
                        className="flex items-center space-x-2 text-slate-300 hover:text-white cursor-pointer"
                      >
                        <Wrench className="w-4 h-4" />
                        <span>{t('nav.creatorTools')}</span>
                      </Link>
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem asChild>
                        <Link 
                          to="/admin" 
                          className="flex items-center space-x-2 text-slate-300 hover:text-white cursor-pointer"
                        >
                          <Shield className="w-4 h-4" />
                          <span>{t('nav.admin')}</span>
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator className="bg-slate-700" />
                    <DropdownMenuItem 
                      onClick={signOut}
                      className="flex items-center space-x-2 text-red-400 hover:text-red-300 cursor-pointer"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>{t('nav.logout')}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="flex items-center gap-1.5">
                  <AuthDialog defaultTab="signin">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-slate-300 hover:text-white hover:bg-slate-800 font-mono text-xs px-2"
                    >
                      {t('nav.login')}
                    </Button>
                  </AuthDialog>
                  <AuthDialog defaultTab="signup">
                    <Button 
                      size="sm"
                      className="bg-gradient-to-r from-gaming-primary to-gaming-secondary hover:from-gaming-primary/80 hover:to-gaming-secondary/80 text-white font-mono text-xs px-2"
                    >
                      {t('nav.signup')}
                    </Button>
                  </AuthDialog>
                </div>
              )}
            </div>

            {/* Menu Hamburger Mobile + Tablette */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="md:hidden text-slate-300 hover:text-white"
                  aria-label={language === 'fr' ? 'Ouvrir le menu' : language === 'es' ? 'Abrir menú' : 'Open menu'}
                >
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent 
                side="right" 
                className="w-[280px] bg-slate-900 border-slate-800"
              >
                <div className="flex flex-col space-y-6 pt-6 overflow-y-auto max-h-[calc(100vh-4rem)] pr-2">
                  {/* Recherche mobile */}
                  <div className="sm:hidden">
                    <SearchDropdown />
                  </div>

                  {/* Navigation */}
                  <nav className="flex flex-col space-y-4">
                    <Link 
                      to={getHomePath(language)} 
                      className="text-slate-300 hover:text-white transition-colors py-2 border-b border-slate-800"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t('nav.home')}
                    </Link>
                    <Link 
                      to={getLeaderboardPath(language)} 
                      className="text-slate-300 hover:text-white transition-colors py-2 border-b border-slate-800"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t('nav.topVoters')}
                    </Link>
                    {user && (
                      <>
                        <Link 
                          to={getFavoritesPath(language)} 
                          className="text-slate-300 hover:text-white transition-colors py-2 border-b border-slate-800"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {t('nav.favorites')}
                        </Link>
                        <Link 
                          to={getMyVotesPath(language)} 
                          className="text-slate-300 hover:text-white transition-colors py-2 border-b border-slate-800"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {t('nav.myVotes')}
                        </Link>
                        <Link 
                          to={getCreatorToolsPath(language)}
                          className="text-slate-300 hover:text-white transition-colors py-2 border-b border-slate-800"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <div className="flex items-center space-x-2">
                            <Wrench className="w-4 h-4" />
                            <span>{t('nav.creatorTools')}</span>
                          </div>
                        </Link>
                        <Link 
                          to={getProfilePath(language)} 
                          className="text-slate-300 hover:text-white transition-colors py-2 border-b border-slate-800"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <div className="flex items-center space-x-2">
                            <Settings className="w-4 h-4" />
                            <span>{t('nav.profile')}</span>
                          </div>
                        </Link>
                        {isAdmin && (
                          <Link 
                            to="/admin" 
                            className="text-slate-300 hover:text-white transition-colors py-2 border-b border-slate-800"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <div className="flex items-center space-x-2">
                              <Shield className="w-4 h-4" />
                              <span>{t('nav.admin')}</span>
                            </div>
                          </Link>
                        )}
                      </>
                    )}
                  </nav>

                  {/* Language Selector mobile */}
                  <div className="sm:hidden border-t border-slate-800 pt-4">
                    <LanguageSelector />
                  </div>

                  {/* Auth mobile + tablette */}
                  <div className="lg:hidden border-t border-slate-800 pt-4">
                    {user ? (
                      <Button 
                        onClick={() => {
                          signOut();
                          setMobileMenuOpen(false);
                        }}
                        variant="ghost"
                        className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-slate-800"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        {t('nav.logout')}
                      </Button>
                    ) : (
                      <div className="flex flex-col space-y-2">
                        <AuthDialog defaultTab="signin">
                          <Button 
                            variant="ghost" 
                            className="w-full text-slate-300 hover:text-white hover:bg-slate-800"
                          >
                            {t('nav.login')}
                          </Button>
                        </AuthDialog>
                        <AuthDialog defaultTab="signup">
                          <Button 
                            className="w-full bg-gradient-to-r from-gaming-primary to-gaming-secondary hover:from-gaming-primary/80 hover:to-gaming-secondary/80 text-white"
                          >
                            {t('nav.signup')}
                          </Button>
                        </AuthDialog>
                      </div>
                    )}
                  </div>

                  {/* Reset countdown mobile */}
                  <div className="xl:hidden border-t border-slate-800 pt-4">
                    <MonthlyResetCountdown variant="mini" />
                  </div>
                </div>
              </SheetContent>
            </Sheet>

            {/* Barre de recherche - positionnée tout à droite avec taille optimale */}
            <div className="hidden md:block w-[240px] lg:w-[280px] xl:w-[320px] 2xl:w-[360px] flex-shrink-0">
              <SearchDropdown />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
